import { Lightbulb, LightbulbOff } from 'lucide-react'
import { useMemo, type ReactNode } from 'react'

import { actionHintsToggled, selectActionHintsEnabled } from '@krp/client-runtime'
import { ActionInfoContext, Button, type ActionInfoSource } from '@krp/ui-kit'

import { ACTION_REGISTRY } from '../composition/actions'
import './actionHints.css'
import { useAppDispatch, useAppSelector } from './hooks'
import type { RootState } from './types'

const selectEnabled = (state: RootState): boolean => selectActionHintsEnabled(state.actionHints)

/**
 * Подсказки ⓘ (UI-ACTIONS): ui-kit store не знает, поэтому описание кнопок и
 * переключатель «Подсказки» приходят ему контекстом отсюда. Описания — реестр
 * действий приложения; в разработке кнопка с `actionId` без записи помечается
 * «нет описания».
 */
export function ActionInfoBridge({ children }: { children: ReactNode }) {
  const enabled = useAppSelector(selectEnabled)
  const source = useMemo<ActionInfoSource>(
    () => ({ enabled, describe: ACTION_REGISTRY.describe, audit: import.meta.env.DEV }),
    [enabled],
  )
  return <ActionInfoContext.Provider value={source}>{children}</ActionInfoContext.Provider>
}

/**
 * Переключатель «Подсказки» в шапке: нажатое состояние — `aria-pressed`, значок
 * (горит / погашен) и слово «вкл/выкл», а не только цвет (CLAUDE.md §7). Выбор —
 * настройка устройства. Вид — как у кнопок шапки UI-STYLE: малый размер, подпись в
 * `shell-header__label` (на телефоне её читает только диктор, остаётся значок).
 */
export function HintsToggle() {
  const enabled = useAppSelector(selectEnabled)
  const dispatch = useAppDispatch()
  return (
    <Button
      variant={enabled ? 'secondary' : 'ghost'}
      data-size="sm"
      aria-pressed={enabled}
      icon={enabled ? <Lightbulb size={14} aria-hidden="true" /> : <LightbulbOff size={14} aria-hidden="true" />}
      title={enabled ? 'Подсказки ⓘ у кнопок включены. Нажмите, чтобы выключить' : 'Показать подсказки ⓘ у кнопок'}
      onClick={() => dispatch(actionHintsToggled())}
      actionId="shell.hints"
    >
      <span className="shell-header__label">
        Подсказки
        <span aria-hidden="true">{enabled ? ': вкл' : ': выкл'}</span>
      </span>
    </Button>
  )
}
